import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import { AdminContext } from "../../Context/AdminContext"
import logoo from "../../imges/logo.png"
import { FiAlignJustify } from "react-icons/fi";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";

function ProfileAdmin() {
    let { logOut } = useContext(AdminContext)
    const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 768); // Sidebar open for desktop by default
    const [isLoading, setIsLoading] = useState(false); // Add loading state
    let navigate = useNavigate();
    const [selectedImage, setSelectedImage] = useState(null); // To hold the selected file
    const [previewImage, setPreviewImage] = useState("assets/img/profile-img.jpg"); // Default preview image
    const [isUploading, setIsUploading] = useState(false); // Uploading state
    const [selectedCoverImage, setSelectedCoverImage] = useState(null);
    const [previewCoverImage, setPreviewCoverImage] = useState("assets/img/default-cover.jpg");
    const [isCoverUploading, setIsCoverUploading] = useState(false);
    const { t, i18n } = useTranslation(); // Translation hook
    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    useEffect(() => {
        const handleResize = () => {
            setIsSidebarOpen(window.innerWidth > 768); // Open sidebar on desktop, close on mobile
        };

        window.addEventListener("resize", handleResize);

        // Cleanup listener on component unmount
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };
    function getAdmin() {
        return axios.get(`https://together-v.com/api/v1/agent/getAgent`, {
            withCredentials: true
        });
    }

    let { data } = useQuery('getAdmin', getAdmin);
    const handlePasswordChange = async (values) => {
        setIsLoading(true); // Set loading to true when submitting

        try {
            const response = await axios.post(
                `https://together-v.com/api/v1/agent/updatePasswordByAgent`,
                {
                    oldPass: values.currentPassword,
                    newPass: values.newPassword,
                },
                { withCredentials: true }
            );

            if (response.data.message === "Password updated successfully") {
                toast.success("Password updated successfully!");
            } else {
                toast.error("Failed to update password");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "An error occurred");
        }
        finally {
            setIsLoading(false); // Set loading to false after completion
        }
    };

    const passwordFormik = useFormik({
        initialValues: {
            currentPassword: "",
            newPassword: "",
            reenterPassword: "",
        },
        validationSchema: Yup.object({
            currentPassword: Yup.string()
                .required("Current password is required"),
            newPassword: Yup.string()
                .required("New password is required")
                .min(6, "Password must be at least 6 characters long"),
            reenterPassword: Yup.string()
                .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
                .required("Please re-enter your new password"),
        }),
        onSubmit: handlePasswordChange,
    });

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedImage(file);

        // Generate a preview
        const reader = new FileReader();
        reader.onload = (event) => {
            setPreviewImage(event.target.result);
        };
        reader.readAsDataURL(file);

        // Automatically upload after selecting the file
        await uploadProfileImage(file);
    };

    const uploadProfileImage = async (file) => {
        const formData = new FormData();
        formData.append("img", file);

        try {
            setIsUploading(true); // Start loader

            const response = await axios.post(
                "https://together-v.com/api/v1/agent/profileImage",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("AgentToken")}`,
                    },
                    withCredentials: true,
                }
            );

            if (response.data?.message === "Cover image product updated successfully") {
                toast.success("Profile image uploaded successfully!");
            } else {
                toast.error("Failed to upload the image.");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "An error occurred during upload.");
        } finally {
            setIsUploading(false); // Stop loader
        }
    };

    const handleCoverFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setSelectedCoverImage(file);

        // Generate a preview
        const reader = new FileReader();
        reader.onload = (event) => {
            setPreviewCoverImage(event.target.result);
        };
        reader.readAsDataURL(file);

        // Automatically upload after selecting the file
        await uploadCoverImage(file);
    };

    // Upload cover image function
    const uploadCoverImage = async (file) => {
        const formData = new FormData();
        formData.append("img", file);

        try {
            setIsCoverUploading(true); // Start loader

            const response = await axios.post(
                "https://together-v.com/api/v1/agent/backgroundImage",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${localStorage.getItem("AgentToken")}`, // Add token to the request
                    },
                    withCredentials: true,
                }
            );

            if (response.data?.message === "Background image product updated successfully") {
                toast.success("Cover image uploaded successfully!");
            } else {
                toast.error("Failed to upload the cover image.");
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "An error occurred during upload.");
        } finally {
            setIsCoverUploading(false); // Stop loader
        }
    };
    useEffect(() => {
        if (i18n.language === "ar") {
            document.body.classList.add("rtl");
        } else {
            document.body.classList.remove("rtl");
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove("rtl");
        };
    }, [i18n.language]);

    return (
        <>
            <header id="header" className="header fixed-top d-flex align-items-center">
                <Helmet>
                    <meta charset="utf-8" />
                    <meta content="width=device-width, initial-scale=1.0" name="viewport" />

                    <title>Profile</title>
                    <meta content="" name="description" />
                    <meta content="" name="keywords" />
                </Helmet>
                <div className="d-flex align-items-center justify-content-between w-100">
                    {/* Logo */}
                    <div>
                        <img
                            src={logoo}
                            alt="Logo"
                            className="logo d-flex align-items-center w-35"
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate("/DashboardAdmin")}
                        />
                    </div>

                    {/* Language Toggle Buttons */}
                    <div className="d-flex">
                        <li className="nav-item dropdown bg-body">
                            <Link
                                className="nav-link "
                                to="#"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                            >
                                <span>{t("language")}</span>
                            </Link>
                            <ul className="dropdown-menu">
                                <li className="nav-item">
                                    <button
                                        className={`btn ${i18n.language === "en" ? "btn-primary" : "btn-outline-primary"} mx-1`}
                                        onClick={() => changeLanguage("en")}
                                    >
                                        English
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`btn ${i18n.language === "ar" ? "btn-primary" : "btn-outline-primary"} mx-1`}
                                        onClick={() => changeLanguage("ar")}
                                    >
                                        العربية
                                    </button>
                                </li>

                            </ul>
                        </li>

                    </div>

                    {/* Sidebar Toggle */}
                    <h2 className="menu-icon" onClick={toggleSidebar}>
                        <FiAlignJustify />
                    </h2>
                </div>
            </header>


            <aside id="sidebar" className={`sidebar ${isSidebarOpen ? "open" : "closed"} ${i18n.language === "ar" ? "sidebar-right" : ""}`}>
                <ul className="sidebar-nav" id="sidebar-nav">
                    <li className="nav-item">
                        <Link className="nav-link" to="/DashboardAdmin">
                            <i className="bi bi-grid"></i>
                            <span>{t("dashboard")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/Agents">
                            <i className="bi bi-grid"></i>
                            <span>{t("allAgents")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/NewId">
                            <i className="bi bi-grid"></i>
                            <span>{t("changeID")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/AddEpin">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("addEpin")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/EPin">
                            <i className="bi bi-grid"></i>
                            <span>E-Pins</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/EpinHistory">
                            <i className="bi bi-grid"></i>
                            <span>{t("ePinsHistory")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/AddProduct">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("addProduct")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/AllProducts">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("allProducts")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/MyTreeAdmin">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("myTree")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/AddEvent">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("addEvents")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/ViewAllEventsAdmin">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("viewEvents")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/WithDrawRequest">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("withdrawRequests")}</span>
                        </Link>
                    </li>

                    <li className="nav-item dropdown bg-body">
                        <Link
                            className="nav-link dropdown-toggle"
                            to="#"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("reports")}</span>
                        </Link>
                        <ul className="dropdown-menu">
                            <li className="nav-item">
                                <Link className="nav-link collapsed" to="/MyTransfers">
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span>{t("myTransfers")}</span>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link collapsed" to="/AllRequestsProducts">
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span>{t("allPurchase")}</span>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link collapsed" to="/TransferRequests">
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span>{t("allTransfers")}</span>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link collapsed" to="/AllWithDrawsAdmin">
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span>{t("allWithdraws")}</span>
                                </Link>
                            </li>
                            <li className="nav-item">
                                <Link className="nav-link collapsed" to="/BinaryReports">
                                    <i className="bi bi-box-arrow-in-down"></i>
                                    <span>{t("binaryDetails")}</span>
                                </Link>
                            </li>
                        </ul>
                    </li>


                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/Filtration">
                            <i className="bi bi-box-arrow-in-down"></i>
                            <span>{t("filtration")}</span>
                        </Link>
                    </li>
                    <li className="nav-heading">{t("pages")}</li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/AllContactUs">
                            <i className="bi bi-person"></i>
                            <span>{t("allContact")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" to="/ProfileAdmin">
                            <i className="bi bi-person"></i>
                            <span>{t("profile")}</span>
                        </Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link collapsed" onClick={() => logOut()}>
                            <i className="bi bi-person"></i>
                            <span>{t("logout")}</span>
                        </Link>
                    </li>
                </ul>
            </aside>

            <main id="main" className="main bg-light-2">
                <div className="pagetitle">
                    <h1>{t("profile")}</h1>
                    <nav>
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <a href="index.html">{t("home")}</a>
                            </li>
                            <li className="breadcrumb-item">{t("users")}</li>
                            <li className="breadcrumb-item active">{t("profile")}</li>
                        </ol>
                    </nav>
                </div>

                <section className="section profile">
                    <div className="col-xl-6 mt-4">
                        <h5>{t("coverImage")}</h5>
                        <img
                            id="coverImagePreview"
                            src={data?.data.data.backgroundImage || previewCoverImage}
                            alt="Cover"
                            className="rounded"
                            style={{ width: "100%", height: "200px", objectFit: "cover" }}
                        />

                        {/* File Input */}
                        <input
                            type="file"
                            id="coverImageInput"
                            accept="image/*"
                            style={{ display: "none" }}
                            onChange={handleCoverFileChange}
                        />

                        {/* Button */}
                        <button
                            type="button"
                            className="button blue-1 border py-2 px-3 mt-3"
                            onClick={() => {
                                // Trigger file selection
                                document.getElementById("coverImageInput").click();
                            }}
                        >
                            {isCoverUploading ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                                t("changeUploadCoverImage")
                            )}
                        </button>
                    </div>
                    <div className="row justify-content-between">
                        <div class="col-xl-4 mt-5">
                            <div class="card border-0 mb-xl-0 mb-3 mt-5">
                                <div class="card-body profile-card pt-4 d-flex flex-column align-items-center">
                                    <img
                                        id="profileImagePreview"
                                        src={data?.data.data.profileImage
                                        }
                                        alt="Profile"
                                        className="rounded-circle"
                                        style={{ width: "100px", height: "100px", objectFit: "cover" }}
                                    />

                                    {/* File Input */}
                                    <input
                                        type="file"
                                        id="profileImageInput"
                                        accept="image/*"
                                        style={{ display: "none" }}
                                        onChange={handleFileChange}
                                    />

                                    {/* Buttons */}
                                    <button
                                        type="button"
                                        className="button blue-1 border py-2 px-3 mt-3"
                                        onClick={() => {
                                            // Trigger file selection
                                            document.getElementById("profileImageInput").click();
                                        }}
                                    >
                                        {isUploading ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            t("changeUploadImage")
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="col-xl-8">
                            <div className="card border-0">
                                <div className="card-body p-3">
                                    <ul className="nav nav-tabs border-bottom" role="tablist">
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className="nav-link active"
                                                id="overview-tap"
                                                data-bs-toggle="pill"
                                                data-bs-target="#profile-overview"
                                                type="button"
                                                role="tab"
                                                aria-controls="profile-overview"
                                                aria-selected="true"
                                            >
                                                {t("overview")}
                                            </button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className="nav-link"
                                                id="edit-tap"
                                                data-bs-toggle="pill"
                                                data-bs-target="#profile-edit"
                                                type="button"
                                                role="tab"
                                                aria-controls="profile-edit"
                                                aria-selected="false"
                                                tabIndex="-1"
                                            >
                                               {t("editProfile")}
                                            </button>
                                        </li>
                                        <li className="nav-item" role="presentation">
                                            <button
                                                className="nav-link"
                                                id="change-password"
                                                data-bs-toggle="pill"
                                                data-bs-target="#profile-change-password"
                                                type="button"
                                                role="tab"
                                                aria-controls="profile-change-password"
                                                aria-selected="false"
                                                tabIndex="-1"
                                            >
                                                {t("changePassword")}
                                            </button>
                                        </li>
                                    </ul>
                                    <div className="tab-content pt-2">
                                        <div
                                            className="tab-pane fade profile-overview show active"
                                            id="profile-overview"
                                            role="tabpanel"
                                            aria-labelledby="#overview-tap"
                                        >
                                            <h5 className="card-title py-3 mb-2">{t("profileDetails")}</h5>
                                            <div className="row mb-3">
                                                <div className="col-lg-3 col-md-4 label">{t("fullName")}</div>
                                                <div className="col-lg-9 col-md-8">{data?.data.data.firstName} {data?.data.data.secondName} {data?.data.data.thirdName} {data?.data.data.lastName}</div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-lg-3 col-md-4 label">{t("country")}</div>
                                                <div className="col-lg-9 col-md-8">{data?.data.data.country}</div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-lg-3 col-md-4 label">{t("phone")}</div>
                                                <div className="col-lg-9 col-md-8">{data?.data.data.phone}</div>
                                            </div>
                                            <div className="row mb-3">
                                                <div className="col-lg-3 col-md-4 label">{t("email")}</div>
                                                <div className="col-lg-9 col-md-8">{data?.data.data.email}</div>
                                            </div>
                                        </div>


                                        <div className="tab-pane fade profile-edit pt-3" id="profile-edit" role="tabpanel" aria-labelledby="#edit-tap">
                                            <form
                                                onSubmit={async (e) => {
                                                    e.preventDefault();
                                                    const updatedData = {
                                                        firstName: e.target.firstName.value,
                                                        secondName: e.target.secondName.value,
                                                        thirdName: e.target.thirdName.value,
                                                        lastName: e.target.lastName.value,
                                                        email: e.target.email.value,
                                                        phone: e.target.phone.value,
                                                        country: e.target.country.value,
                                                    };

                                                    try {
                                                        const token = localStorage.getItem("AgentToken"); // Assuming the token is stored in localStorage

                                                        const { data } = await axios.post(
                                                            `https://together-v.com/api/v1/agent/updateAgent`,
                                                            updatedData,
                                                            {
                                                                withCredentials: true,
                                                                headers: {
                                                                    Authorization: `Bearer ${token}`,
                                                                },
                                                            }
                                                        );

                                                        if (data.message === "Agent updated successfully") {
                                                            toast.success("Profile updated successfully");
                                                        }
                                                    } catch (err) {
                                                        toast.error(err);
                                                    }
                                                }}
                                            >
                                                <div className="row mb-3">
                                                    <label htmlFor="firstName" className="col-md-4 col-lg-3 col-form-label">{t("firstName")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="firstName"
                                                            type="text"
                                                            className="form-control"
                                                            id="firstName"
                                                            defaultValue={data?.data.data.firstName || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                    <label htmlFor="secondName" className="col-md-4 col-lg-3 col-form-label">{t("secondName")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="secondName"
                                                            type="text"
                                                            className="form-control"
                                                            id="secondName"
                                                            defaultValue={data?.data.data.secondName || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                    <label htmlFor="thirdName" className="col-md-4 col-lg-3 col-form-label">{t("thirdName")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="thirdName"
                                                            type="text"
                                                            className="form-control"
                                                            id="thirdName"
                                                            defaultValue={data?.data.data.thirdName || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                    <label htmlFor="lastName" className="col-md-4 col-lg-3 col-form-label">{t("lastName")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="lastName"
                                                            type="text"
                                                            className="form-control"
                                                            id="lastName"
                                                            defaultValue={data?.data.data.lastName || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                    <label htmlFor="country" className="col-md-4 col-lg-3 col-form-label">{t("country")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="country"
                                                            type="text"
                                                            className="form-control"
                                                            id="country"
                                                            defaultValue={data?.data.data.country || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                    <label htmlFor="phone" className="col-md-4 col-lg-3 col-form-label">{t("phone")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="phone"
                                                            type="text"
                                                            className="form-control"
                                                            id="phone"
                                                            defaultValue={data?.data.data.phone || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-3">
                                                    <label htmlFor="email" className="col-md-4 col-lg-3 col-form-label">{t("email")}</label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="email"
                                                            type="email"
                                                            className="form-control"
                                                            id="email"
                                                            defaultValue={data?.data.data.email || ""}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="text-center">
                                                    <button type="submit" className="button dark-1 bg-blue-1 md text-white">{t("saveChanges")}</button>
                                                </div>
                                            </form>
                                        </div>

                                        <div
                                            className="tab-pane fade profile-change-password pt-3"
                                            id="profile-change-password"
                                            role="tabpanel"
                                            aria-labelledby="#change-password"
                                        >
                                            <form onSubmit={passwordFormik.handleSubmit}>
                                                <div className="row mb-3">
                                                    <label htmlFor="currentPassword" className="col-md-4 col-lg-3 col-form-label">
                                                    {t("current_password")}
                                                    </label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="currentPassword"
                                                            type="password"
                                                            className="form-control"
                                                            id="currentPassword"
                                                            value={passwordFormik.values.currentPassword}
                                                            onChange={passwordFormik.handleChange}
                                                            onBlur={passwordFormik.handleBlur}
                                                        />
                                                        {passwordFormik.touched.currentPassword && passwordFormik.errors.currentPassword && (
                                                            <div className="text-danger">{passwordFormik.errors.currentPassword}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="row mb-3">
                                                    <label htmlFor="newPassword" className="col-md-4 col-lg-3 col-form-label">
                                                    {t("new_password")}
                                                    </label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="newPassword"
                                                            type="password"
                                                            className="form-control"
                                                            id="newPassword"
                                                            value={passwordFormik.values.newPassword}
                                                            onChange={passwordFormik.handleChange}
                                                            onBlur={passwordFormik.handleBlur}
                                                        />
                                                        {passwordFormik.touched.newPassword && passwordFormik.errors.newPassword && (
                                                            <div className="text-danger">{passwordFormik.errors.newPassword}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="row mb-3">
                                                    <label htmlFor="reenterPassword" className="col-md-4 col-lg-3 col-form-label">
                                                    {t("reenter_new_password")}
                                                    </label>
                                                    <div className="col-md-8 col-lg-9">
                                                        <input
                                                            name="reenterPassword"
                                                            type="password"
                                                            className="form-control"
                                                            id="reenterPassword"
                                                            value={passwordFormik.values.reenterPassword}
                                                            onChange={passwordFormik.handleChange}
                                                            onBlur={passwordFormik.handleBlur}
                                                        />
                                                        {passwordFormik.touched.reenterPassword && passwordFormik.errors.reenterPassword && (
                                                            <div className="text-danger">{passwordFormik.errors.reenterPassword}</div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="text-center">
                                                    <button type="submit" className="btn btn-primary" disabled={isLoading}>
                                                        {isLoading ? (
                                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        ) : (
                                                            t("change_password")
                                                        )}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>


        </>
    );
}

export default ProfileAdmin;
